/**
 * Rowted Admin, inside the app's own frame — `docs/rowted-admin-v2.md` §0.1, §4.1.
 *
 * **One frame, one word different.** The console used to be a dark bar and a
 * centred column that looked like a different product, on the theory that an
 * employee should never mistake it for a customer's account. The theory was
 * right and the answer was wrong: what actually tells you where you are is the
 * black **ADMIN** tag beside the mark and the environment chip after it, and
 * everything else being familiar is what makes the screens usable. A separate
 * visual language also meant a separate set of components, which is why the
 * console's clients list was a hand-rolled table while the office app had a
 * list engine.
 *
 * So this is the office frame's shape — 56px header, the same rail metrics,
 * `PageBar` underneath — assembled from the same pieces rather than copied:
 * `RowtedMark` from `logo.tsx`, `NavRow` from `nav.tsx`, `PageBar` as it is.
 * What it does *not* share is `Header` itself, and deliberately: that
 * component is built around `MeResponse` — a tenant, a scope switcher, a
 * notification feed, a theme toggle — and an admin has none of those. Passing
 * a fake `MeResponse` to reuse it would have been the flag-through-the-middle
 * this file exists to avoid.
 */
import { useEffect, useRef, useState, type ReactNode } from 'react'
import { PLATFORM_ROLE_LABELS, type PlatformRole } from '@rowted/shared'
import { cn } from '../lib/cn.js'
import { RowtedMark } from './logo.js'
import { NavRow, type NavBadge } from './nav.js'
import { AccountMenu, type MenuRowSpec } from './account-menu.js'
import { NotificationBell, NotificationsPanel } from './notifications.js'
import { Toast } from '../components/overlays.js'
import { useEnvironments } from './use-environments.js'
import { adminRequest } from '../lib/admin-api.js'
import { Avatar } from '../components/primitives.js'
import { applyTheme, chooseTheme, readTheme, type Theme } from '../lib/theme.js'
import { usePhone } from '../lib/use-narrow.js'
import {
  IconAdminUsers, IconAuditLog, IconChevronLeft, IconChevronRight, IconClients, IconClose,
  IconHealth, IconIntegrations, IconMenu, IconNext, IconSearch, IconSettings,
} from './icons.js'

const COLLAPSED_KEY = 'rowted.admin.nav.collapsed'

export type AdminNavKey =
  'clients' | 'integrations' | 'admins' | 'settings' | 'health' | 'audit'

export interface AdminNavItem {
  key: AdminNavKey
  label: string
  icon: typeof IconClients
  /** Where the row goes. Kept here so the rail and the router cannot disagree. */
  path: string
}

/**
 * Six rows, fixed.
 *
 * Not filtered by role, unlike the office rail. §2.3 gives every admin role
 * `read` on all six screens; what the role changes is what the screens *offer*
 * — an Edit button, a Save — and that is decided inside them from
 * `me.abilities`. A rail that hid Integrations from a support account would
 * hide the status they are allowed and expected to look at.
 */
export const ADMIN_NAV: readonly AdminNavItem[] = [
  { key: 'clients', label: 'Clients', icon: IconClients, path: '/admin/clients' },
  { key: 'integrations', label: 'Integrations', icon: IconIntegrations, path: '/admin/integrations' },
  { key: 'admins', label: 'Admin users', icon: IconAdminUsers, path: '/admin/admins' },
  { key: 'settings', label: 'Settings', icon: IconSettings, path: '/admin/settings' },
  { key: 'health', label: 'Health', icon: IconHealth, path: '/admin/health' },
  { key: 'audit', label: 'Audit logs', icon: IconAuditLog, path: '/admin/audit' },
] as const

export interface AdminUser {
  id: string
  name: string
  /** The two columns behind `name`, for My profile's fields. */
  firstName?: string
  lastName?: string
  mobilePhone?: string | null
  email: string
  role: PlatformRole
  abilities: string[]
  steppedUpAt: string | null
  /**
   * The client this address also has an account at, or null for almost
   * everybody — `docs/rowted-admin-v2.md` §2.6.
   *
   * Their *own* account there, not a client they support: signing in signs
   * them in as themselves, so there is no impersonation banner and no consent
   * to ask for. The menu no longer reads it — Environments
   * (`identity-and-sign-in.md` §2) lists every place this address can reach,
   * of which this is one — but `GET /platform/me` still sends it, and a caller
   * that wants that one case should not have to filter the list for it.
   */
  ownTenant?: { id: string; name: string } | null
  /**
   * This session's CSRF token, re-armed on every load.
   *
   * Sign-in hands it over once and the client keeps it in `sessionStorage`,
   * which is per tab — so a second tab had the cookie and no token. See the
   * note on `GET /platform/me`.
   */
  csrfToken?: string
}

export interface AdminFrameProps {
  me: AdminUser
  active: AdminNavKey | null
  badges?: Partial<Record<AdminNavKey, NavBadge>>
  children: ReactNode
  onNavigate: (path: string) => void
  onSignOut: () => void
  /** "v2.14 · 3 jobs running", from `/health`. Absent until it has loaded. */
  status?: string
  /**
   * How many are waiting — the same prop the office header takes.
   *
   * Nothing raises an admin notification yet, so this is 0 in practice and
   * the panel says where the rows will come from. The bell is here anyway
   * because Aaron asked for it and because the alternative — adding the
   * control at the same time as the first feed — is how one app ends up with
   * a header the other does not have.
   */
  unreadCount?: number
}

/**
 * Which deployment this is.
 *
 * From the build's own mode rather than from an API call, because the answer
 * has to be right on the first paint — the whole job of this chip is to be
 * read before somebody does something to a customer. `VITE_ROWTED_ENV`
 * overrides it for a staging build that is otherwise a production build.
 */
function environment(): { label: string; tone: 'live' | 'quiet' } {
  const named = import.meta.env.VITE_ROWTED_ENV as string | undefined
  if (named) {
    return { label: named, tone: /prod/i.test(named) ? 'live' : 'quiet' }
  }
  return import.meta.env.PROD
    ? { label: 'Production', tone: 'live' }
    : { label: 'Development', tone: 'quiet' }
}

export function AdminFrame({
  me, active, badges, children, onNavigate, onSignOut, status, unreadCount = 0,
}: AdminFrameProps) {
  const [theme, setTheme] = useState<Theme>(() => readTheme())
  const [collapsed, setCollapsed] = useState(() => {
    const stored = localStorage.getItem(COLLAPSED_KEY)
    if (stored !== null) return stored === 'true'
    return window.matchMedia('(max-width: 1100px)').matches
  })
  const phone = usePhone()
  const [navOpen, setNavOpen] = useState(false)
  /* One at a time, the office header's rule: opening either closes the other. */
  const [open, setOpen] = useState<'notifications' | 'user' | null>(null)
  const toggle = (panel: 'notifications' | 'user') =>
    setOpen((current) => (current === panel ? null : panel))
  const menuAt = useRef<HTMLButtonElement>(null)
  const bellAt = useRef<HTMLDivElement>(null)
  const {
    environments, current, switchTo, error: switchError, dismissError,
  } = useEnvironments(adminRequest)

  useEffect(() => { if (!phone) setNavOpen(false) }, [phone])
  useEffect(() => applyTheme(theme), [theme])
  useEffect(() => localStorage.setItem(COLLAPSED_KEY, String(collapsed)), [collapsed])

  const env = environment()
  const go = (path: string) => { onNavigate(path); setNavOpen(false) }


  /*
   * The account menu's Notifications row, and **only where there is no bell**
   * (Aaron: "notifications can be reached through the bell icon").
   *
   * On a desktop that is true and the row was a second door to a room with
   * one already, so it goes. On a phone the bell is not rendered at all — it
   * folds into this menu to give the search box its 36px back — so dropping
   * the row everywhere would leave a phone with no way to notifications. The
   * caller deciding is what keeps it to one rule: exactly one way to the bell
   * at every width.
   */
  const menuBell = phone
    ? { notifications: { count: unreadCount, onOpen: () => setOpen('notifications') } }
    : {}

  return (
    <div className="flex h-app flex-col bg-bg">
      <header className={cn(
        'relative flex h-header shrink-0 items-center gap-2 border-b border-line',
        'bg-surface px-3 sm:gap-3 sm:px-4',
      )}>
        {phone && (
          <button
            type="button"
            onClick={() => setNavOpen(true)}
            aria-label="Menu"
            aria-expanded={navOpen}
            className={cn('-ml-1 flex h-10 w-10 shrink-0 items-center justify-center',
              'rounded-field text-text hover:bg-hover')}
          >
            <IconMenu size={20} />
          </button>
        )}

        <span className="flex shrink-0 items-center gap-[7px]">
          <RowtedMark />
          <b className="hidden text-[15px] tracking-[-0.02em] text-text sm:block">rowted</b>
          <AdminTag />
        </span>

        <span aria-hidden className="hidden h-[22px] w-px shrink-0 bg-line sm:block" />

        {/*
          * Which deployment. A dot rather than a word for the tone, because
          * "Production" is the word and repeating it in a colour name would
          * not add anything a person reads.
          */}
        <span className={cn(
          'hidden h-[22px] shrink-0 items-center gap-1.5 rounded-chip px-2 text-tag',
          'font-semibold sm:inline-flex',
          env.tone === 'live'
            ? 'bg-accent-soft text-accent-dark'
            : 'bg-line-soft text-text-muted')}
        >
          <span aria-hidden className={cn('h-[6px] w-[6px] rounded-full',
            env.tone === 'live' ? 'bg-accent' : 'bg-text-faint')} />
          {env.label}
        </span>

        <button
          type="button"
          aria-label="Search"
          className={cn(
            'flex h-9 min-w-0 flex-1 items-center gap-2 rounded-field border border-line bg-field',
            'px-2.5 text-text-muted hover:border-line-strong sm:ml-2 sm:px-3 lg:max-w-[420px]')}
        >
          <span className="shrink-0"><IconSearch size={16} /></span>
          <span className="hidden flex-1 truncate text-left text-body sm:inline">
            Search clients, people, tenants…
          </span>
          <kbd className={cn('hidden rounded-tag border border-line px-1.5 py-px',
            'text-tag text-text-faint lg:inline')}
          >
            ⌘K
          </kbd>
        </button>

        <div className="ml-auto flex shrink-0 items-center gap-0.5 sm:gap-1">
          {status && (
            <span className="hidden text-tag tabular-nums text-text-faint xl:inline">
              {status}
            </span>
          )}

          {/* On a phone it folds into the menu, exactly as it does in the
              office app — the 36px is the difference between a search box you
              can read and one you cannot. */}
          {!phone && (
            <NotificationBell
              ref={bellAt}
              count={unreadCount}
              open={open === 'notifications'}
              onToggle={() => toggle('notifications')}
            />
          )}

          <div className="relative sm:ml-1">
            <button
              ref={menuAt}
              type="button"
              onClick={() => toggle('user')}
              aria-expanded={open === 'user'}
              className={cn('inline-flex h-[38px] items-center gap-2 rounded-full py-0',
                'pl-1 pr-1.5 hover:bg-hover')}
            >
              <span className="relative flex">
                <Avatar name={me.name} self size={30} />
                {/* The bell's badge, when there is no bell. */}
                {phone && unreadCount > 0 && (
                  <i aria-hidden className={cn('absolute -right-0.5 -top-0.5 h-2.5 w-2.5',
                    'rounded-full border-[1.5px] border-surface bg-danger')} />
                )}
              </span>
              <span className="hidden flex-col text-left leading-[1.15] lg:flex">
                <b className="text-[12.5px]">{me.name}</b>
                <span className="text-tag font-normal text-text-muted">
                  {PLATFORM_ROLE_LABELS[me.role]} · MFA on
                </span>
              </span>
            </button>

            <AccountMenu
              open={open === 'user'}
              onClose={() => setOpen(null)}
              anchor={menuAt.current}
              who={{
                name: me.name,
                email: me.email,
                /* Board 1's third line, the console's version of it. */
                line: `${PLATFORM_ROLE_LABELS[me.role]} · Rowted Admin · MFA on`,
              }}
              theme={theme}
              /* A pick from the menu is the one thing remembered (step 27). */
              onThemeChange={(next) => { chooseTheme(next); setTheme(next) }}
              /* `/admin/account`, not the admin-users record this used to
                 open: that one is for an owner looking at somebody else, and
                 has nothing on it you can do to your own credentials. */
              profileHref="/admin/account" 
              onGo={go}
              onSignOut={onSignOut}
              environments={environments}
              current={current}
              onSwitch={(target) => { void switchTo(target) }}
              {...menuBell}
              rows={adminMenuRows(me, go, badges?.health)}
            />

            {/* Hung off whichever control opened it — the bell on a desktop,
                the avatar on a phone, where the bell is a menu row instead. */}
            <NotificationsPanel
              open={open === 'notifications'}
              onClose={() => setOpen(null)}
              anchor={phone ? menuAt.current : bellAt.current}
            />
          </div>
        </div>
      </header>

      {/* A switch that did not happen has to say so: the menu has already
          closed by the time the server answers, so nothing else would. */}
      {switchError && (
        <div className="fixed bottom-4 left-1/2 z-50 -translate-x-1/2">
          <Toast tone="danger" onDismiss={dismissError}>{switchError}</Toast>
        </div>
      )}

      <div className="flex min-h-0 flex-1">
        {phone
          ? <AdminNavSheet
              active={active}
              badges={badges ?? {}}
              me={me}
              open={navOpen}
              onClose={() => setNavOpen(false)}
              onNavigate={go}
            />
          : <AdminRail
              active={active}
              badges={badges ?? {}}
              me={me}
              collapsed={collapsed}
              onToggleCollapsed={() => setCollapsed((value) => !value)}
              onNavigate={go}
            />}
        <main className="relative min-w-0 flex-1 overflow-y-auto">{children}</main>
      </div>
    </div>
  )
}

/** The black tag that says which app this is. One place, one definition. */
export function AdminTag() {
  return (
    <span className={cn('inline-flex h-5 shrink-0 items-center rounded-button bg-text px-[7px]',
      'text-tag font-bold uppercase tracking-[0.04em] text-bg')}
    >
      Admin
    </span>
  )
}

interface RailProps {
  me: AdminUser
  active: AdminNavKey | null
  badges: Partial<Record<AdminNavKey, NavBadge>>
  onNavigate: (path: string) => void
}

function AdminRail({
  me, active, badges, collapsed, onToggleCollapsed, onNavigate,
}: RailProps & { collapsed: boolean; onToggleCollapsed: () => void }) {
  return (
    <nav
      aria-label="Rowted Admin"
      className={cn(
        'flex shrink-0 flex-col gap-0.5 border-r border-line bg-surface pt-2.5',
        'transition-[width]',
        collapsed ? 'w-nav-collapsed' : 'w-nav')}
    >
      {!collapsed && (
        <span className="mx-2 mb-1 px-3 text-label uppercase tracking-[0.06em] text-text-faint">
          Rowted platform
        </span>
      )}

      {ADMIN_NAV.map((item) => (
        <NavRow
          key={item.key}
          label={item.label}
          icon={item.icon}
          active={item.key === active}
          collapsed={collapsed}
          badge={badges[item.key]}
          onClick={() => onNavigate(item.path)}
        />
      ))}

      {/*
        * The footer says who you are here and offers the one thing this rail
        * is missing: a way into a customer's app. §4.1 puts it on Clients,
        * because signing in as somebody starts from choosing the somebody.
        */}
      <div className="mt-auto flex flex-col gap-1 p-2">
        {!collapsed && (
          <>
            <span className="px-1 text-sub text-text-faint">
              Signed in as a platform {PLATFORM_ROLE_LABELS[me.role].toLowerCase()}
            </span>
            <button
              type="button"
              onClick={() => onNavigate('/admin/clients')}
              className={cn('flex items-center gap-1 px-1 text-cell font-semibold',
                'text-accent hover:text-accent-dark')}
            >
              Open the app as a client
              <IconNext size={13} />
            </button>
          </>
        )}
        <button
          type="button"
          onClick={onToggleCollapsed}
          className={cn(
            'flex h-[38px] items-center gap-2.5 rounded-button px-3',
            'text-body font-medium text-text-muted hover:bg-hover',
            collapsed && 'justify-center px-0')}
        >
          {collapsed ? <IconChevronRight size={18} /> : <IconChevronLeft size={18} />}
          {!collapsed && <span>Collapse</span>}
        </button>
      </div>
    </nav>
  )
}

/** The same rows, over the page, on a phone — `frame/nav.tsx`'s arrangement. */
function AdminNavSheet({
  me, active, badges, open, onClose, onNavigate,
}: RailProps & { open: boolean; onClose: () => void }) {
  if (!open) return null

  return (
    <div className="fixed inset-0 z-50 flex bg-black/25" onClick={onClose}>
      <div
        role="dialog"
        aria-modal="true"
        aria-label="Rowted Admin"
        onClick={(event) => event.stopPropagation()}
        className="flex h-full w-[272px] max-w-[82vw] flex-col bg-surface shadow-slideover"
      >
        <header className={cn('flex h-header shrink-0 items-center justify-between',
          'border-b border-line pl-4 pr-2')}
        >
          <span className="flex items-center gap-2">
            <span className="text-card text-text">Rowted</span>
            <AdminTag />
          </span>
          <button
            type="button"
            aria-label="Close menu"
            onClick={onClose}
            className={cn('flex h-9 w-9 items-center justify-center rounded-field',
              'text-text-muted hover:bg-hover')}
          >
            <IconClose size={18} />
          </button>
        </header>

        <nav
          aria-label="Rowted Admin"
          className="flex min-h-0 flex-1 flex-col gap-0.5 overflow-y-auto py-2 pb-safe"
        >
          {ADMIN_NAV.map((item) => (
            <NavRow
              key={item.key}
              label={item.label}
              icon={item.icon}
              active={item.key === active}
              collapsed={false}
              touch
              badge={badges[item.key]}
              onClick={() => onNavigate(item.path)}
            />
          ))}
          <span className="mt-auto px-5 pb-2 pt-3 text-sub text-text-faint">
            Signed in as a platform {PLATFORM_ROLE_LABELS[me.role].toLowerCase()}
          </span>
        </nav>
      </div>
    </div>
  )
}

/**
 * The rows Rowted Admin contributes to the shared account menu.
 *
 * Board 1 frame 2: Admin users, Settings, Health — and Health carries what is
 * wrong as its tag, which is the one number somebody opening this menu wants
 * without navigating to find it.
 */
function adminMenuRows(
  me: AdminUser, go: (path: string) => void, health?: NavBadge,
): MenuRowSpec[] {
  const rows: MenuRowSpec[] = [{
    label: 'Admin users',
    icon: IconAdminUsers,
    sub: 'who can sign in here',
    onClick: () => go('/admin/admins'),
  }, {
    label: 'Settings',
    icon: IconSettings,
    onClick: () => go('/admin/settings'),
  }]
  if (me.abilities.includes('health')) {
    rows.push({
      label: 'Health',
      icon: IconHealth,
      /* The same badge the nav row wears, so the menu and the rail cannot
         disagree about how many things are wrong. */
      ...(health ? { tag: health.text } : {}),
      onClick: () => go('/admin/health'),
    })
  }
  return rows
}

